import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Grid, Chip, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import {
  CheckCircle, ArrowForward, ExpandMore,
  Rocket, Api, Speed, Lock, AdminPanelSettings,
  DomainVerification, AutoGraph, Email,
} from '@mui/icons-material';
import PublicLayout from '../components/layout/PublicLayout';

const stats = [
  { value: '5 min', label: 'Domain Setup' },
  { value: '∞', label: 'Scales With You' },
  { value: 'REST', label: 'Full API Access' },
  { value: '99.9%', label: 'Uptime SLA' },
];

const pillars = [
  {
    color: '#7c3aed',
    icon: <Api sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'API-First from Day One',
    desc: 'CruvzMail exposes a full REST API for user provisioning, sending, and mailbox management. Automate your email operations the same way you automate everything else.',
    points: [
      'REST API for user & domain management',
      'Programmatic email sending',
      'Webhook support for delivery events',
      'Token-based authentication (JWT)',
    ],
  },
  {
    color: '#dc2626',
    icon: <AutoGraph sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Grows as You Scale',
    desc: 'Start with 5 mailboxes and add more as your team grows. No upfront commitment — you only pay for what you use, and billing adjusts automatically every cycle.',
    points: [
      'Add mailboxes instantly, any time',
      'Per-mailbox pricing — no wasted seats',
      'No upfront provisioning fees',
      'Upgrade plans without migration',
    ],
  },
  {
    color: '#0891b2',
    icon: <Speed sx={{ fontSize: 28, color: '#fff' }} />,
    title: 'Ship Fast, Look Professional',
    desc: 'Get off @gmail the moment you incorporate. Custom domain email, white-label webmail with your branding, and a team admin console — all live in minutes.',
    points: [
      'user@yourstartup.com from day 1',
      'White-label login page with your logo',
      'Shared inboxes for team@ and support@',
      'Mobile-optimised webmail + Android app',
    ],
  },
];

const features = [
  { icon: <DomainVerification sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Custom Domain Email', desc: 'Professional @yourcompany.com from the moment you sign up.' },
  { icon: <Api sx={{ fontSize: 22, color: '#0891b2' }} />, title: 'REST API', desc: 'Full programmatic access — create users, send mail, manage domains.' },
  { icon: <AdminPanelSettings sx={{ fontSize: 22, color: '#dc2626' }} />, title: 'Admin Console', desc: 'Manage every user and domain from a central dashboard.' },
  { icon: <Lock sx={{ fontSize: 22, color: '#059669' }} />, title: 'Security Built-In', desc: 'SPF, DKIM, DMARC, TLS 1.3, and Rspamd spam filter — auto-configured.' },
  { icon: <Email sx={{ fontSize: 22, color: '#d97706' }} />, title: 'Transactional Email', desc: 'Use the API to send product notifications, alerts, and verifications.' },
  { icon: <Rocket sx={{ fontSize: 22, color: '#7c3aed' }} />, title: 'Instant Provisioning', desc: 'New employee? New mailbox in 30 seconds — no IT ticket needed.' },
];

const faqs = [
  { q: 'Can I use the API to create users programmatically?', a: 'Yes. CruvzMail exposes REST API endpoints for creating, updating, and deleting users, managing domains, querying mailbox quotas, and triggering email sends. Full API documentation is available at cruvzmail.com/documentation.' },
  { q: 'We\'re a small team right now — can we start with just 3 mailboxes?', a: 'Absolutely. There\'s no minimum mailbox requirement. Start with 3 (or even 1) and scale up as you hire. Billing is per active mailbox per month.' },
  { q: 'How is CruvzMail different from Google Workspace for startups?', a: 'CruvzMail is self-contained in Indian infrastructure — your data stays in India, you own your domain setup, and pricing doesn\'t jump 3× when you exit startup-tier Google discounts. You also get a REST API, white-label branding, and full admin control without paying enterprise prices.' },
  { q: 'Can I send transactional email (user verifications, notifications) through CruvzMail?', a: 'Yes. Use our SMTP relay or REST API to send application-triggered emails from your product. Your sender domain is properly authenticated with DKIM/SPF, so transactional messages land in the inbox.' },
  { q: 'What happens when we raise a round and hire 50 people fast?', a: 'Just add mailboxes from the admin panel — instantly, no downtime. You can also use the API to bulk-provision users from your HR system. Plans scale with you and pricing adjusts automatically.' },
];

const StartupsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <PublicLayout activeNav="For Business">

      {/* ── HERO ── */}
      <Box sx={{
        pt: { xs: '120px', md: '145px' }, pb: { xs: 10, md: 14 }, px: { xs: 2, md: 5 },
        position: 'relative', overflow: 'hidden', textAlign: 'center',
        background: 'linear-gradient(160deg, #0f172a 0%, #2e1065 50%, #0f172a 100%)',
      }}>
        <Box sx={{ position: 'absolute', top: -180, right: -120, width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(124,58,237,0.16) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: -80, left: -80, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(8,145,178,0.1) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 860, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Chip label="For Startups" sx={{ mb: 3, bgcolor: 'rgba(124,58,237,0.15)', color: '#c4b5fd', fontWeight: 700, fontSize: '0.78rem', border: '1px solid rgba(124,58,237,0.3)', letterSpacing: '0.06em' }} />
          <Typography variant="h1" sx={{
            fontWeight: 900, fontSize: { xs: '2.4rem', md: '3.6rem' }, lineHeight: 1.1, mb: 3,
            background: 'linear-gradient(135deg, #ffffff 30%, #c4b5fd 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            Email Built for Startups —<br />From Day One
          </Typography>
          <Typography sx={{ fontSize: { xs: '1rem', md: '1.15rem' }, color: 'rgba(255,255,255,0.6)', maxWidth: 620, mx: 'auto', lineHeight: 1.85, mb: 5 }}>
            Ship faster with a REST API for provisioning, custom domain email on your own infrastructure, and pricing that scales with your headcount — not against it.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#7c3aed', boxShadow: '0 0 40px rgba(124,58,237,0.4)', '&:hover': { bgcolor: '#6d28d9' } }}>
              Start for Free
            </Button>
            <Button onClick={() => navigate('/documentation')} sx={{ px: 4, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Browse the API Docs
            </Button>
          </Box>
        </Box>
      </Box>

      {/* ── STATS ── */}
      <Box sx={{ bgcolor: '#0f172a', py: 3.5, px: { xs: 2, md: 5 }, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
          <Grid container spacing={2} justifyContent="center">
            {stats.map((s) => (
              <Grid size={{ xs: 6, md: 3 }} key={s.label}>
                <Box sx={{ textAlign: 'center', py: 1 }}>
                  <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', md: '2rem' }, color: '#c4b5fd', lineHeight: 1 }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', fontWeight: 500, mt: 0.4 }}>{s.label}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── PILLARS ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 7 }}>
            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.12em', color: '#94a3b8', mb: 1.5 }}>Why Startups Choose CruvzMail</Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.8rem', md: '2.4rem' }, mb: 1.5 }}>
              Built for the Way Startups Work
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '1rem', maxWidth: 500, mx: 'auto' }}>
              An API, cost that scales with headcount, and professional email from day one.
            </Typography>
          </Box>
          <Grid container spacing={3}>
            {pillars.map((p, i) => (
              <Grid size={{ xs: 12, md: 4 }} key={p.title}>
                <Box sx={{
                  height: '100%', p: { xs: 3, md: 4 }, borderRadius: 3.5,
                  border: '1px solid #e2e8f0', bgcolor: '#fff', position: 'relative', overflow: 'hidden',
                  transition: 'all 0.25s',
                  '&:hover': { borderColor: p.color, boxShadow: `0 8px 40px ${p.color}12`, transform: 'translateY(-3px)' },
                }}>
                  <Typography sx={{ position: 'absolute', top: -14, right: 14, fontWeight: 900, fontSize: '5.5rem', color: `${p.color}09`, lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>
                    {String(i + 1).padStart(2, '0')}
                  </Typography>
                  <Box sx={{ width: 50, height: 50, borderRadius: 2.5, bgcolor: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5, boxShadow: `0 6px 20px ${p.color}30` }}>
                    {p.icon}
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: '1.1rem', color: '#0f172a', mb: 1.2 }}>{p.title}</Typography>
                  <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.8, mb: 2.5 }}>{p.desc}</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.9 }}>
                    {p.points.map((pt) => (
                      <Box key={pt} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ fontSize: 15, color: p.color, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#475569', fontWeight: 500 }}>{pt}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FEATURES ── */}
      <Box sx={{ py: { xs: 8, md: 10 }, px: { xs: 2, md: 5 }, bgcolor: '#fff' }}>
        <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' }, mb: 1 }}>
              Everything in the Box
            </Typography>
            <Typography sx={{ color: '#64748b', fontSize: '0.95rem' }}>No add-ons. No per-feature pricing. Every capability included.</Typography>
          </Box>
          <Grid container spacing={2.5}>
            {features.map((f) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={f.title}>
                <Box sx={{ p: 3, border: '1px solid #e2e8f0', borderRadius: '14px', height: '100%', bgcolor: '#fafafa', transition: 'all 0.2s', '&:hover': { borderColor: '#cbd5e1', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } }}>
                  <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#0f172a', mb: 0.6 }}>{f.title}</Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>{f.desc}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Box>

      {/* ── FAQ ── */}
      <Box sx={{ py: { xs: 8, md: 12 }, px: { xs: 2, md: 5 }, bgcolor: '#f8fafc' }}>
        <Box sx={{ maxWidth: 760, mx: 'auto' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', fontSize: { xs: '1.7rem', md: '2.2rem' } }}>Startup FAQ</Typography>
          </Box>
          {faqs.map((faq, i) => (
            <Accordion key={i} elevation={0} disableGutters sx={{ border: '1px solid #e2e8f0', borderRadius: '12px !important', mb: 1.5, overflow: 'hidden', '&:before': { display: 'none' }, '&.Mui-expanded': { boxShadow: '0 4px 20px rgba(0,0,0,0.06)', borderColor: '#ddd6fe' } }}>
              <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#64748b' }} />} sx={{ px: 3, py: 0.5, '&.Mui-expanded': { bgcolor: '#faf5ff' } }}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#0f172a', py: 0.5 }}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 3, pb: 2.5, bgcolor: '#faf5ff' }}>
                <Typography sx={{ fontSize: '0.88rem', color: '#64748b', lineHeight: 1.85 }}>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>

      {/* ── CTA ── */}
      <Box sx={{ py: { xs: 10, md: 12 }, px: { xs: 2, md: 6 }, bgcolor: '#0f172a', textAlign: 'center', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(124,58,237,0.1) 0%, transparent 60%)', pointerEvents: 'none' }} />
        <Box sx={{ maxWidth: 620, mx: 'auto', position: 'relative', zIndex: 1 }}>
          <Typography variant="h2" sx={{ fontWeight: 900, fontSize: { xs: '2rem', md: '2.8rem' }, color: '#fff', mb: 2, lineHeight: 1.15 }}>
            Ready to Launch on<br />Your Own Domain?
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: 'rgba(255,255,255,0.5)', mb: 5, lineHeight: 1.85 }}>
            5-minute setup. REST API from day one. Pricing that won't punish your growth.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button onClick={() => navigate('/signup')} endIcon={<ArrowForward />} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', bgcolor: '#7c3aed', '&:hover': { bgcolor: '#6d28d9' } }}>
              Get Started
            </Button>
            <Button onClick={() => navigate('/documentation')} sx={{ px: 4.5, py: 1.6, borderRadius: '12px', fontSize: '1rem', fontWeight: 700, textTransform: 'none', color: '#fff', border: '1px solid rgba(255,255,255,0.2)', bgcolor: 'rgba(255,255,255,0.05)', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}>
              Read the Docs
            </Button>
          </Box>
        </Box>
      </Box>

    </PublicLayout>
  );
};

export default StartupsPage;
